import { useEffect, useState } from "react";
import { format } from "date-fns";
import axiosInstance from "../../api/axiosInstance";
import Select from "react-select";
import Loader from "../../common/Loader";
import formatDateIndian from "../../common/formatDateIndian";

const WindowReport = () => {
  const [fromDate, setFromDate] = useState("2024-01-01");
  const [toDate, setToDate] = useState("2025-12-31");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);

  const [salePersonData, setSalePersonData] = useState([]);
  const [siteData, setSiteData] = useState([]);
  const [saleStageData, setStageData] = useState([]);

  const [selectedSalePerson, setSelectedSalePerson] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);

  const fetchSalePersons = async () => {
    try {
      const response = await axiosInstance.get("User/GetAllUser");
      setSalePersonData(response.data);
    } catch (error) {
      console.error("Failed to fetch sale persons:", error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axiosInstance.get("SiteDetail/GetAll");
      setSiteData(response.data);
    } catch (error) {
      console.error("Failed to fetch sites:", error);
    }
  };

  const fetchStage = async () => {
    try {
      const response = await axiosInstance.get("Report/GetAllSiteStages");
      setStageData(response.data);
    } catch (error) {
      console.error("Failed to fetch stages:", error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const payload = {
        fromDate,
        toDate,
        sale_Person_Id: selectedSalePerson
          ? parseInt(selectedSalePerson.value)
          : null,
        site_Id: selectedSite ? parseInt(selectedSite.value) : null,
        stage_Id: selectedStage ? parseInt(selectedStage.value) : null,
      };
      const response = await axiosInstance.post(
        "Report/GetFilteredWindowQuotations",
        payload
      );
      setReportData(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    fetchSalePersons();
    fetchSites();
    fetchStage();
  }, []);

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: "180px",
      fontSize: "14px",
      borderColor: "#CBD5E0",
      boxShadow: "none",
    }),
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-4">
        Window Quotation Report
      </h2>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div>
          <label className="text-sm text-gray-600 font-medium">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full px-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div>
          <label className="text-sm text-gray-600 font-medium">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full px-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div>
          <label className="text-sm text-gray-600 font-medium">
            Sale Person
          </label>
          <Select
            options={salePersonData.map((per) => ({
              value: String(per.sale_Person_Id),
              label: per.Sale_Person_Name,
            }))}
            value={selectedSalePerson}
            onChange={setSelectedSalePerson}
            isClearable
            styles={selectStyles}
          />
        </div>

        <div>
          <label className="text-sm text-gray-600 font-medium">Site</label>
          <Select
            options={siteData.map((site) => ({
              value: String(site.site_Id),
              label: site.site_Name,
            }))}
            value={selectedSite}
            onChange={setSelectedSite}
            isClearable
            styles={selectStyles}
          />
        </div>

        <div>
          <label className="text-sm text-gray-600 font-medium">Stage</label>
          <Select
            options={saleStageData.map((stage) => ({
              value: String(stage.stage_ID),
              label: stage.stage_Name,
            }))}
            value={selectedStage}
            onChange={setSelectedStage}
            isClearable
            styles={selectStyles}
          />
        </div>

        <div className="self-end">
          <button
            onClick={fetchReport}
            className="bg-secondary text-white px-5 py-2 rounded-md hover:bg-secondary-dark transition"
          >
            Search
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Loader />
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-primary text-white">
              <tr>
                <th className="px-4 py-2 text-left">Quotation No</th>
                <th className="px-4 py-2 text-left">Quotation Date</th>
                <th className="px-4 py-2 text-left">Site Name</th>
                <th className="px-4 py-2 text-left">Sale Person Name</th>
                <th className="px-4 py-2 text-left">Total SQ.</th>
                <th className="px-4 py-2 text-left">Stage</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {reportData.length > 0 ? (
                reportData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{item.quotation_No}</td>
                    <td className="px-4 py-2">
                      {formatDateIndian(item.quotation_Date)}
                    </td>
                    <td className="px-4 py-2">{item.site_Name}</td>
                    <td className="px-4 py-2">{item.sale_Person_Name}</td>
                    <td className="px-4 py-2">{item.total_Sqft}</td>
                    <td className="px-4 py-2">{item.stage_Name}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center text-gray-500 py-4 font-medium"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WindowReport;
